import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Modal, Form, Input, Button, Switch, Select, Spin, message, Divider, Alert, Badge } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';

const formLayout = {
  labelCol: {
    sm: { span: 5 },
  },
  wrapperCol: {
    sm: { span: 16 },
  },
  labelWrap: true,
};

const formTailLayout = {
  wrapperCol: {
    sm: { offset: 5, span: 16 },
  },
};

const { Option } = Select;

export default function StationZoneIoEditModal(props) {
  const {
    stationId,
    zones,
    zoneId,
    zoneIoId,
    zoneIo = {},
    visible,
    onUpdate,
    onOk,
    onCancel,
  } = props;
  const zoneIoConfigsData = useSelector(state => state.zoneIoConfigs);

  const [loading, setLoading] = useState(false);

  const [selectedType, setSelectedType] = useState(zoneIo ? zoneIo.type : null);
  const [zoneIoSettingsHasChanged, setZoneIoSettingsHasChanged] = useState({});

  // flatten settings
  const zoneIoFlatten = (() => {
    const flatten = {
      ...zoneIo,
    };

    if (zoneIo) {
      delete flatten.settings;

      for (let key in zoneIo.settings) {
        flatten[`settings.${key}`] = zoneIo.settings[key];
      }
    }

    return flatten;
  })();

  const onFinish = async (values) => {
    setLoading(true);

    const settingConfigs = zoneIoConfigsData.find(zoneIoConfig => zoneIoConfig.type === selectedType).settings;

    const zoneIoObj = {
      stationId,
      zoneId,
      name: values.name,
      type: values.type,
    };

    const zoneIoSettingsObj = {};

    // io settings
    for (let settingConfig of settingConfigs) {
      if (zoneIoSettingsHasChanged[`settings.${settingConfig.key}`]) {
        let value = values[`settings.${settingConfig.key}`];

        switch (settingConfig.type) {
          case 'string':
          case 'option': {
            value = value !== undefined ? value : null;
            break;
          }
          case 'number':
            value = isNaN(parseFloat(value)) ? null : parseFloat(value);
            break;
        }

        zoneIoSettingsObj[settingConfig.key] = value;
      }
    }

    // edit
    if (zoneIoId) {
      onUpdate({
        name: 'ZONE_IO',
        action: 'UPDATE',
        id: zoneIoId,
        data: zoneIoObj,
      });

      for (let key in zoneIoSettingsObj) {
        onUpdate({
          name: 'ZONE_IO_SETTING',
          action: 'UPDATE',
          id: zoneIoId,
          key,
          value: zoneIoSettingsObj[key],
        });
      }
    } else {
      const tempId = `#${Math.random().toString().slice(2)}`;

      onUpdate({
        name: 'ZONE_IO',
        action: 'CREATE',
        id: tempId,
        data: zoneIoObj,
      });

      for (let key in zoneIoSettingsObj) {
        onUpdate({
          name: 'ZONE_IO_SETTING',
          action: 'UPDATE',
          id: tempId,
          key,
          value: zoneIoSettingsObj[key],
        });
      }
    }

    onOk();
  };

  const onFieldsChange = (changedFields, allFields) => {
    if (changedFields) {
      setZoneIoSettingsHasChanged({
        ...zoneIoSettingsHasChanged,
        [changedFields[0].name[0]]: true,
      });
    }
  };

  const renderSettings = () => {
    const settingConfigs = zoneIoConfigsData.find(zoneIoConfig => zoneIoConfig.type === selectedType).settings;

    const hasTargetValue = !['max', 'min', 'targetValueSafePercent', 'targetValue'].some(key => !settingConfigs.some(settingConfig => settingConfig.key === key));

    return [
      ...(settingConfigs.length ? [
        <Divider key="divider" />,
        hasTargetValue && (
          <>
            <Form.Item
              {...formTailLayout}
              shouldUpdate
            >
              {({ getFieldValue }) => {
                const max = parseFloat(getFieldValue('settings.max'));
                const min = parseFloat(getFieldValue('settings.min'));
                const safePercent = parseFloat(getFieldValue('settings.targetValueSafePercent'));
                const targetValue = parseFloat(getFieldValue('settings.targetValue'));

                if (!isNaN(max) && !isNaN(min) && max > min && !isNaN(safePercent) && !isNaN(targetValue)) {
                  const range = max - min;

                  const clamp = (value) => Math.max(min, Math.min(value, max));

                  const targetLowerWarningValue = clamp(targetValue - range * safePercent);
                  const targetLowerSafeValue = clamp(targetValue - range * safePercent / 2);
                  const targetUpperSafeValue = clamp(targetValue + range * safePercent / 2);
                  const targetUpperWarningValue = clamp(targetValue + range * safePercent);

                  const decimal = Math.max(0, 2 - Math.floor(Math.log(max) / Math.log(10)));

                  const labels = {
                    'danger': [],
                    'warning': [],
                    'safe': [],
                  };

                  if (targetLowerWarningValue.toFixed(decimal) !== min.toFixed(decimal)) {
                    labels['danger'].push(`< ${targetLowerWarningValue.toFixed(decimal)}`);
                  }
                  if (targetLowerSafeValue.toFixed(decimal) !== targetLowerWarningValue.toFixed(decimal)) {
                    labels['warning'].push(`${targetLowerWarningValue.toFixed(decimal)} - ${targetLowerSafeValue.toFixed(decimal)}`);
                  }
                  if (targetUpperSafeValue.toFixed(decimal) !== targetLowerSafeValue.toFixed(decimal)) {
                    labels['safe'].push(`${targetLowerSafeValue.toFixed(decimal)} - ${targetUpperSafeValue.toFixed(decimal)}`);
                  }
                  if (targetUpperWarningValue.toFixed(decimal) !== targetUpperSafeValue.toFixed(decimal)) {
                    labels['warning'].push(`${targetUpperSafeValue.toFixed(decimal)} - ${targetUpperWarningValue.toFixed(decimal)}`);
                  }
                  if (max.toFixed(decimal) !== targetUpperWarningValue.toFixed(decimal)) {
                    labels['danger'].push(`> ${targetUpperWarningValue.toFixed(decimal)}`);
                  }

                  return (
                    <div className="station-setting-zone-io-target-value-container">
                      <div>
                        <div className="danger" style={{
                          width: `${(targetLowerWarningValue - min) / range * 100}%`,
                        }} />
                        <div className="warning" style={{
                          width: `${(targetLowerSafeValue - targetLowerWarningValue) / range * 100}%`,
                        }} />
                        <div className="safe" style={{
                          width: `${(targetUpperSafeValue - targetLowerSafeValue) / range * 100}%`,
                        }} />
                        <div className="warning" style={{
                          width: `${(targetUpperWarningValue - targetUpperSafeValue) / range * 100}%`,
                        }} />
                        <div className="danger" style={{
                          width: `${(max - targetUpperWarningValue) / range * 100}%`,
                        }} />
                      </div>
                      {labels['danger'].length > 0 && (
                        <Badge status="error" text={labels['danger'].join(' or ')} />
                      )}
                      {labels['warning'].length > 0 && (
                        <Badge status="warning" text={labels['warning'].join(' or ')} />
                      )}
                      {labels['safe'].length > 0 && (
                        <Badge status="success" text={labels['safe'].join(' or ')} />
                      )}
                    </div>
                  );
                }

                return (
                  <Alert
                    message="Enter all the target values to illustrate the safety range"
                    type="info"
                  />
                );
              }}
            </Form.Item>
            <Divider key="divider" />
          </>
        ),
      ] : []),
      ...settingConfigs.map((item, i) => {
        switch (item.type) {
          case 'number': {
            return (
              <Form.Item
                {...formLayout}
                key={item.key}
                label={item.title}
                name={`settings.${item.key}`}
                rules={[
                  ...(item.isRequired ? [{
                    required: true,
                    message: `Please enter ${item.title}`,
                  }] : []),
                ]}
                initialValue={item.defaultValue}
              >
                <Input
                  type="number"
                  className={zoneIoSettingsHasChanged[`settings.${item.key}`] && 'input-has-changed'}
                />
              </Form.Item>
            );
          }
          case 'string': {
            return (
              <Form.Item
                {...formLayout}
                key={item.key}
                label={item.title}
                name={`settings.${item.key}`}
                rules={[
                  ...(item.isRequired ? [{
                    required: true,
                    message: `Please enter ${item.title}`,
                  }] : []),
                ]}
                initialValue={item.defaultValue}
              >
                <Input
                  className={zoneIoSettingsHasChanged[`settings.${item.key}`] && 'input-has-changed'}
                />
              </Form.Item>
            );
          }
          case 'boolean': {
            return (
              <Form.Item
                {...formLayout}
                key={item.key}
                label={item.title}
                name={`settings.${item.key}`}
                rules={[
                  ...(item.isRequired ? [{
                    required: true,
                    message: `Please select ${item.title}`,
                  }] : []),
                ]}
                valuePropName="checked"
                initialValue={item.defaultValue}
              >
                <Switch
                  className={zoneIoSettingsHasChanged[`settings.${item.key}`] && 'input-has-changed'}
                />
              </Form.Item>
            );
          }
          case 'option': {
            return (
              <Form.Item
                {...formLayout}
                key={item.key}
                label={item.title}
                name={`settings.${item.key}`}
                rules={[
                  ...(item.isRequired ? [{
                    required: true,
                    message: `Please select ${item.title}`,
                  }] : []),
                ]}
                initialValue={item.defaultValue}
              >
                <Select
                  className={zoneIoSettingsHasChanged[`settings.${item.key}`] && 'input-has-changed'}
                  placeholder="Not selected"
                >
                  {item.options.map((option) => {
                    return (
                      <Option
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            );
          }
          default:
            return (
              <span>(${item.key})</span>
            );
        }
      }),
    ];
  };

  const typeOptions = zoneIoConfigsData.map(zoneIoConfig => {
    return {
      label: zoneIoConfig.type,
      value: zoneIoConfig.type,
    };
  });

  return (
    <Modal
      title={zoneIoId ? `Edit Zone IO (${zoneIoId.substr(0, 8)})` : 'Add Zone IO'}
      visible={visible}
      onOk={onOk}
      onCancel={onCancel}
      width="80%"
      maskClosable={false}
      footer={null}
    >
      <Spin spinning={loading}>
        <Form
          {...formLayout}
          initialValues={zoneIoFlatten}
          onFinish={onFinish}
          onFieldsChange={onFieldsChange}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[
              {
                required: true,
                message: 'Please enter the name',
              },
              ({getFieldValue}) => ({
                validator(_, value) {
                  const zone = zones.find(zone => zone.id === zoneId);
                  if (zone) {
                    const isExisting = zone.ios.some(io => io.name === value && io.id !== zoneIoId);

                    if (isExisting) {
                      return Promise.reject('Duplicate IO name in the same zone');
                    }
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input
              placeholder="Name"
            />
          </Form.Item>

          <Form.Item
            label="Type"
            name="type"
            rules={[
              {
                required: true,
                message: 'Please select the type',
              },
            ]}
          >
            <Select
              disabled={!!zoneIoId}
              value={selectedType}
              onChange={(value) => setSelectedType(value)}
            >
              {typeOptions.map(option => (
                <Option value={option.value} key={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {selectedType && renderSettings()}

          <Form.Item {...formTailLayout}>
            <Button type="primary" htmlType="submit" block>
              {zoneIoId ? 'Update' : 'Submit'}
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};